import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getClientById, updateClient } from '../api';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasPermission } from '../utils/permissions';
import InlineLoading from '../components/InlineLoading';
import { notify } from '../utils/alertBus';

const EditClientPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [client, setClient] = useState({
        name: '',
        email: '',
        giro: '',
        rut: '',
        address: '',
        city: '',
        phone: '',
        creditLimit: '',
        creditBlocked: false,
        creditWarningNote: ''
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { user } = useCurrentUser();
    const canUpdateClient = hasPermission(user, 'client.update');
    const canManageCredit = hasPermission(user, 'branch.access.any');

    useEffect(() => {
        const fetchClient = async () => {
            try {
                const data = await getClientById(id);
                setClient(data);
            } catch (err) {
                setError('Error al cargar los datos del cliente.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchClient();
    }, [id]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setClient(prevClient => ({ ...prevClient, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canUpdateClient) return;
        try {
            const payload = { ...client };
            if (canManageCredit) {
                const trimmedLimit = String(client.creditLimit ?? '').trim();
                if (trimmedLimit === '') {
                    payload.creditLimit = null;
                } else {
                    const parsedLimit = Number(trimmedLimit);
                    if (!Number.isFinite(parsedLimit) || parsedLimit < 0) {
                        setError('El limite de credito debe ser un numero valido.');
                        return;
                    }
                    payload.creditLimit = parsedLimit;
                }
                payload.creditBlocked = Boolean(client.creditBlocked);
                payload.creditWarningNote = client.creditWarningNote?.trim() || null;
            } else {
                delete payload.creditLimit;
                delete payload.creditBlocked;
                delete payload.creditWarningNote;
            }
            await updateClient(id, payload);
            notify('Cliente actualizado exitosamente.');
            navigate('/client');
        } catch (err) {
            setError('Error al actualizar el cliente.');
            console.error(err);
        }
    };

    if (loading) {
        return (
            <p className="text-center mt-6">
                <InlineLoading />
            </p>
        );
    }

    return (
        <div className="p-6 bg-gray-100 min-h-screen">
            <div className="max-w-3xl mx-auto bg-white p-6 rounded-lg shadow">
                <h1 className="text-2xl font-bold text-center mb-6">Editar Cliente</h1>
                {error && <p className="text-red-500 text-center">{error}</p>}
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input type="text" name="name" value={client.name} onChange={handleChange} placeholder="Nombre" className="w-full p-3 border border-gray-300 rounded" required disabled={!canUpdateClient} />
                    <input type="email" name="email" value={client.email} onChange={handleChange} placeholder="Email" className="w-full p-3 border border-gray-300 rounded" disabled={!canUpdateClient} />
                    <input type="text" name="giro" value={client.giro || ''} onChange={handleChange} placeholder="Giro" className="w-full p-3 border border-gray-300 rounded" disabled={!canUpdateClient} />
                    <input type="text" name="rut" value={client.rut} onChange={handleChange} placeholder="RUT" className="w-full p-3 border border-gray-300 rounded" required disabled={!canUpdateClient} />
                    <input type="text" name="address" value={client.address} onChange={handleChange} placeholder="Dirección" className="w-full p-3 border border-gray-300 rounded" disabled={!canUpdateClient} />
                    <input type="text" name="city" value={client.city} onChange={handleChange} placeholder="Ciudad" className="w-full p-3 border border-gray-300 rounded" disabled={!canUpdateClient} />
                    <input type="text" name="phone" value={client.phone} onChange={handleChange} placeholder="Teléfono" className="w-full p-3 border border-gray-300 rounded" disabled={!canUpdateClient} />
                    {canManageCredit && (
                        <div className="border-t pt-4 space-y-3">
                            <h2 className="text-sm font-semibold text-gray-700">Credito</h2>
                            <input
                                type="number"
                                min="0"
                                name="creditLimit"
                                value={client.creditLimit ?? ''}
                                onChange={handleChange}
                                placeholder="Limite de credito (opcional)"
                                className="w-full p-3 border border-gray-300 rounded"
                                disabled={!canUpdateClient}
                            />
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    name="creditBlocked"
                                    checked={Boolean(client.creditBlocked)}
                                    onChange={handleChange}
                                    disabled={!canUpdateClient}
                                />
                                Bloquear credito para este cliente
                            </label>
                            <textarea
                                name="creditWarningNote"
                                value={client.creditWarningNote || ''}
                                onChange={handleChange}
                                placeholder="Nota de advertencia"
                                className="w-full p-3 border border-gray-300 rounded"
                                rows={3}
                                disabled={!canUpdateClient}
                            />
                        </div>
                    )}
                    <div className="flex gap-2">
                        {canUpdateClient && (
                          <button type="submit" className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600">Guardar</button>
                        )}
                        <button type="button" onClick={() => navigate('/client')} className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600">Cancelar</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditClientPage;
